import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface SelectOption<T extends string> {
    value: T;
    label: string;
}

interface Props<T extends string> {
    value: T;
    options: SelectOption<T>[];
    onChange: (value: T) => void;
    className?: string;
    minWidth?: number;
}

interface PanelPos {
    left: number;
    top: number;
    width: number;
}

function FluentSelect<T extends string>({
    value,
    options,
    onChange,
    className = '',
    minWidth = 160,
}: Props<T>) {
    const [open, setOpen] = useState(false);
    const [mounted, setMounted] = useState(false);
    const [pos, setPos] = useState<PanelPos | null>(null);
    const wrapRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setMounted(true);
    }, []);

    useLayoutEffect(() => {
        if (!open) return;
        const update = () => {
            const el = wrapRef.current;
            if (!el) return;
            const r = el.getBoundingClientRect();
            setPos({ left: r.left, top: r.bottom + 6, width: r.width });
        };
        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onDocClick = (e: MouseEvent) => {
            const target = e.target as Node;
            if (wrapRef.current?.contains(target)) return;
            if (panelRef.current?.contains(target)) return;
            setOpen(false);
        };
        const onEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onEsc);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onEsc);
        };
    }, [open]);

    const current = options.find((o) => o.value === value) ?? options[0];

    return (
        <div ref={wrapRef} className={`relative inline-block ${className}`} style={{ minWidth }}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className={`w-full flex items-center justify-between gap-2 pl-4 pr-3 py-2.5 rounded-fl text-sm font-semibold text-text_main bg-layer_2 border transition-colors ${
                    open
                        ? 'border-accent bg-layer_3'
                        : 'border-stroke hover:bg-layer_3 hover:border-stroke_strong'
                }`}
            >
                <span className="truncate">{current?.label}</span>
                <svg
                    viewBox="0 0 24 24"
                    className={`w-3 h-3 shrink-0 stroke-text_3 fill-none [stroke-width:2] [stroke-linecap:round] [stroke-linejoin:round] transition-transform ${
                        open ? 'rotate-180' : ''
                    }`}
                >
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>
            {mounted &&
                open &&
                pos &&
                createPortal(
                    <div
                        ref={panelRef}
                        data-outside-ignore="true"
                        className="fixed p-1 rounded-fl-md border border-stroke_strong fluent-scrollbar max-h-72 overflow-auto"
                        style={{
                            left: pos.left,
                            top: pos.top,
                            width: pos.width,
                            zIndex: 1000,
                            background: 'rgba(20,22,28,0.96)',
                            backdropFilter: 'blur(20px) saturate(140%)',
                            WebkitBackdropFilter: 'blur(20px) saturate(140%)',
                            boxShadow: '0 12px 32px rgba(0,0,0,0.55)',
                        }}
                    >
                        {options.map((o) => {
                            const selected = o.value === value;
                            return (
                                <button
                                    key={o.value}
                                    type="button"
                                    onClick={() => {
                                        onChange(o.value);
                                        setOpen(false);
                                    }}
                                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-fl text-[13px] font-medium text-left transition-colors ${
                                        selected
                                            ? 'bg-accent/[.15] text-text_main'
                                            : 'text-text_2 hover:bg-layer_3 hover:text-text_main'
                                    }`}
                                >
                                    {selected && (
                                        <svg
                                            viewBox="0 0 24 24"
                                            className="w-3 h-3 stroke-accent fill-none [stroke-width:2.5] [stroke-linecap:round] [stroke-linejoin:round]"
                                        >
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                    )}
                                    <span className={selected ? '' : 'ml-5'}>{o.label}</span>
                                </button>
                            );
                        })}
                    </div>,
                    document.body
                )}
        </div>
    );
}

export default FluentSelect;
